import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Layers3,
  Cpu,
  UsersRound,
  ShieldCheck,
  Headphones,
} from "lucide-react";

import { softwareDevelopmentData } from "../../../../data/SoftwareDevelopmentData";

// ============================================================
// ICONS
// ============================================================

const icons = [
  BriefcaseBusiness,
  Layers3,
  Cpu,
  UsersRound,
  ShieldCheck,
  Headphones,
];

// ============================================================
// ANIMATIONS
// ============================================================

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
  },

  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

// ============================================================
// WHY CHOOSE US
// ============================================================

const WhyChooseUs = () => {
  const { whyChooseUs } = softwareDevelopmentData;

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        bg-[var(--bg-primary)]
        py-2
        sm:py-4
        md:py-6
        lg:py-10
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-32
          top-24
          h-56
          w-56
          rounded-full
          bg-[color-mix(in_srgb,var(--brand-primary)_5%,transparent)]
          blur-[100px]
          sm:h-64
          sm:w-64
          md:-left-40
          md:h-72
          md:w-72
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-32
          bottom-20
          h-56
          w-56
          rounded-full
          bg-[color-mix(in_srgb,var(--brand-primary)_4%,transparent)]
          blur-[100px]
          sm:h-64
          sm:w-64
          md:-right-40
          md:h-72
          md:w-72
        "
      />

      {/* =====================================================
          SUBTLE CENTER GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          left-1/2
          top-1/3
          h-72
          w-72
          -translate-x-1/2
          rounded-full
          bg-[color-mix(in_srgb,var(--brand-primary)_1.8%,transparent)]
          blur-[120px]
          sm:h-80
          sm:w-80
          md:h-96
          md:w-96
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          md:px-8
          lg:px-8
          xl:px-10
        "
      >
        {/* =================================================
            SECTION HEADER
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-8
            w-full
            max-w-2xl
            text-center
            sm:mb-10
            md:mb-11
            lg:mb-12
          "
        >
          {/* =================================================
              LABEL
          ================================================== */}

          <motion.span
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.4,
            }}
            className="
              inline-flex
              max-w-full
              items-center
              gap-2
              rounded-full
              border
              border-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]
              bg-[color-mix(in_srgb,var(--brand-primary)_8%,transparent)]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              text-[var(--brand-light)]
              sm:px-4
              sm:py-2
              sm:text-xs
              sm:tracking-[2.5px]
            "
          >
            <span
              className="
                h-1.5
                w-1.5
                shrink-0
                rounded-full
                bg-[var(--brand-light)]
              "
            />

            <span className="truncate">
              Why Choose Us
            </span>
          </motion.span>

          {/* =================================================
              HEADING
          ================================================== */}

          <motion.h2
            initial={{
              opacity: 0,
              y: 15,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
              delay: 0.05,
            }}
            className="
              mt-4
              break-words
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              text-[var(--text-primary)]
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          >
            Your Trusted{" "}
            <span className="text-[var(--brand-primary)]">
              Software Partner
            </span>
          </motion.h2>

          {/* =================================================
              DESCRIPTION
          ================================================== */}

          <motion.p
            initial={{
              opacity: 0,
              y: 15,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
              delay: 0.1,
            }}
            className="
              mx-auto
              mt-3
              w-full
              max-w-xl
              break-words
              text-xs
              leading-5
              text-[var(--text-secondary)]
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
          >
            We combine business understanding, modern technology,
            thoughtful design, and dependable engineering to build
            software that delivers lasting value for your business.
          </motion.p>
        </motion.div>

        {/* =================================================
            TRUST INDICATOR
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          whileInView={{
            opacity: 1,
            scale: 1,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.5,
          }}
        />

        {/* =================================================
            FEATURES
        ================================================== */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.1,
          }}
          variants={containerVariants}
          className="
            grid
            w-full
            grid-cols-1
            md:grid-cols-2
            md:gap-x-6
            lg:gap-x-8
          "
        >
          {whyChooseUs.map((item, index) => {
            const Icon = icons[index] || ShieldCheck;

            return (
              <motion.div
                key={item.number}
                variants={itemVariants}
                className="
                  group
                  relative
                  min-w-0
                  border-b
                  border-[color-mix(in_srgb,var(--border-primary)_75%,transparent)]
                  py-5
                  sm:py-6
                  md:py-6
                  lg:py-7
                  md:[&:nth-child(-n+2)]:border-t
                "
              >
                {/* =================================================
                    HOVER BACKGROUND
                ================================================== */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    -inset-x-2
                    inset-y-1
                    rounded-xl
                    bg-[color-mix(in_srgb,var(--brand-primary)_2.5%,transparent)]
                    opacity-0
                    blur-lg
                    transition-opacity
                    duration-300
                    group-hover:opacity-100
                    sm:-inset-x-3
                  "
                />

                {/* =================================================
                    CONTENT ROW
                ================================================== */}

                <div
                  className="
                    relative
                    flex
                    min-w-0
                    items-start
                    gap-2.5
                    sm:gap-3
                    md:gap-4
                  "
                >
                  {/* =================================================
                      NUMBER
                  ================================================== */}

                  <span
                    className="
                      w-6
                      shrink-0
                      pt-1
                      text-[10px]
                      font-semibold
                      tracking-[1.5px]
                      text-[color-mix(in_srgb,var(--brand-primary)_70%,transparent)]
                      sm:w-8
                      sm:text-xs
                      md:w-9
                    "
                  >
                    {item.number}
                  </span>

                  {/* =================================================
                      ICON
                  ================================================== */}

                  <div
                    className="
                      flex
                      h-9
                      w-9
                      shrink-0
                      items-center
                      justify-center
                      rounded-lg
                      border
                      border-[var(--border-primary)]
                      bg-[color-mix(in_srgb,var(--card-bg-secondary)_80%,transparent)]
                      text-[var(--brand-primary)]
                      transition-all
                      duration-300
                      group-hover:border-[color-mix(in_srgb,var(--brand-primary)_30%,transparent)]
                      group-hover:bg-[var(--brand-primary)]
                      group-hover:text-white
                      sm:h-10
                      sm:w-10
                      sm:rounded-xl
                      md:h-11
                      md:w-11
                    "
                  >
                    <Icon
                      size={18}
                      strokeWidth={1.8}
                      className="
                        transition-transform
                        duration-300
                        group-hover:scale-110
                        sm:h-[19px]
                        sm:w-[19px]
                      "
                    />
                  </div>

                  {/* =================================================
                      TEXT CONTENT
                  ================================================== */}

                  <div
                    className="
                      min-w-0
                      flex-1
                    "
                  >
                    {/* =================================================
                        TITLE ROW
                    ================================================== */}

                    <div
                      className="
                        flex
                        min-w-0
                        items-start
                        justify-between
                        gap-2
                        sm:gap-3
                      "
                    >
                      <h3
                        className="
                          min-w-0
                          break-words
                          text-sm
                          font-semibold
                          leading-snug
                          text-[var(--text-primary)]
                          transition-colors
                          duration-300
                          group-hover:text-[var(--brand-light)]
                          sm:text-base
                          md:text-lg
                        "
                      >
                        {item.title}
                      </h3>

                      <ArrowUpRight
                        size={15}
                        className="
                          mt-0.5
                          shrink-0
                          text-[var(--text-muted)]
                          opacity-70
                          transition-all
                          duration-300
                          group-hover:-translate-y-0.5
                          group-hover:translate-x-0.5
                          group-hover:text-[var(--brand-primary)]
                          group-hover:opacity-100
                          sm:h-[17px]
                          sm:w-[17px]
                          md:h-[18px]
                          md:w-[18px]
                        "
                      />
                    </div>

                    {/* =================================================
                        DESCRIPTION
                    ================================================== */}

                    <p
                      className="
                        mt-2
                        w-full
                        max-w-xl
                        break-words
                        text-[11px]
                        leading-5
                        text-[var(--text-muted)]
                        transition-colors
                        duration-300
                        group-hover:text-[var(--text-secondary)]
                        sm:mt-2.5
                        sm:text-xs
                        sm:leading-6
                        md:text-sm
                      "
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* =================================================
            BOTTOM TRUST LINE
        ================================================== */}

        <motion.div
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="
            mt-7
            flex
            w-full
            flex-wrap
            items-center
            justify-center
            gap-2
            px-1
            text-center
            sm:mt-9
            sm:gap-3
          "
        >
          {/* =================================================
              STARS
          ================================================== */}

          <div
            className="
              flex
              shrink-0
              items-center
              gap-0.5
            "
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className="
                  text-xs
                  text-[var(--brand-primary)]
                  sm:text-sm
                "
              >
                ★
              </span>
            ))}
          </div>

          {/* =================================================
              TEXT
          ================================================== */}

          <span
            className="
              max-w-full
              break-words
              text-xs
              leading-5
              text-[var(--text-muted)]
              sm:text-sm
              sm:leading-6
            "
          >
            Professional software solutions you can rely on
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;